import { typographyConfig } from './typography.config';

/**
 * Destructured values from typography configuration.
 */
export const {
  fontFamily,
  styles: styleDefinitions,
  weights
} = typographyConfig;

/**
 * A mapping to convert prefix to human-readable name.
 */
const prefixToNameMapping: Record<string, string> = {
  dp: 'Display',
  h: 'Headings',
  sh: 'Subheadings',
  b: 'Body',
  c: 'Captions'
};

/**
 * Generates a grouped representation of typography styles.
 *
 * This function processes the styles in the typography configuration, groups
 * them based on their prefix (like 'dp', 'h', etc.) and returns an array
 * where each entry represents a group of typography styles.
 *
 * @returns An array of grouped typography styles.
 */
export const generateTypeGroups = () => {
  // Create a mapping of prefixes to their respective styles
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupedStyles: Record<string, any[]> = {};

  Object.entries(styleDefinitions).forEach(([key, styleConfig]) => {
    const prefix = key.replace(/[0-9]/g, '');
    const styleNumber = key.replace(prefix, '');

    // Determine the group name; if prefix is not in mapping, use 'Misc'
    const groupName = prefixToNameMapping[prefix] || 'Misc';
    if (!groupedStyles[groupName]) {
      groupedStyles[groupName] = [];
    }

    const previewName = prefixToNameMapping[prefix] || key; // Fallback to key if prefix not found in mapping
    groupedStyles[groupName]?.push({
      preview: `${previewName} ${styleNumber}`,
      markup: key,
      size: styleConfig.size,
      weight: weights[styleConfig.weight as keyof typeof weights],
      lineHeight: styleConfig.lineHeight
    });
  });

  // Convert the mapping to an array of groups
  return Object.entries(groupedStyles).map(([groupName, styles]) => ({
    name: groupName,
    styles
  }));
};
